import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  Conversation,
  InboxDemoData,
  InboxInteractiveDemoProps,
} from "../data/types";
import {
  cloneDemoData,
  collectExpandedGroupIds,
  conversationMatchesGroup,
  createLocalMessage,
  findGroupById,
  withAppendedMessage,
} from "./demoUtils";

type UseInboxDemoStateArgs = Pick<
  InboxInteractiveDemoProps,
  "data" | "initialConversationId" | "onSendMessage"
> & {
  fallbackData: InboxDemoData;
};

export function useInboxDemoState({
  data,
  fallbackData,
  initialConversationId,
  onSendMessage,
}: UseInboxDemoStateArgs) {
  const sourceData = data ?? fallbackData;

  const [conversations, setConversations] = useState(() =>
    cloneDemoData(sourceData).conversations,
  );
  const [selectedConversationId, setSelectedConversationId] = useState(
    () =>
      initialConversationId ??
      sourceData.conversations[0]?.id ??
      "",
  );
  const [expandedGroupIds, setExpandedGroupIds] = useState(
    () => collectExpandedGroupIds(sourceData.groups),
  );
  const [selectedGroupId, setSelectedGroupId] = useState(
    () => sourceData.selectedGroupId ?? "all",
  );
  const [draft, setDraft] = useState("");
  const messageListRef = useRef<HTMLDivElement | null>(null);
  const conversationListRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const next = cloneDemoData(sourceData);
    setConversations(next.conversations);
    setExpandedGroupIds(collectExpandedGroupIds(next.groups));
    setSelectedGroupId(next.selectedGroupId ?? "all");
    setSelectedConversationId(
      initialConversationId ?? next.conversations[0]?.id ?? "",
    );
    setDraft("");
  }, [sourceData, initialConversationId]);

  const visibleConversations = useMemo(
    () =>
      conversations.filter((item) =>
        conversationMatchesGroup(item, selectedGroupId),
      ),
    [conversations, selectedGroupId],
  );

  const selectedConversation = useMemo(
    () =>
      visibleConversations.find((item) => item.id === selectedConversationId) ??
      visibleConversations[0],
    [visibleConversations, selectedConversationId],
  );

  const scrollMessages = useCallback((position: "top" | "bottom") => {
    const node = messageListRef.current;
    if (!node) return;
    node.scrollTo({
      top: position === "top" ? 0 : node.scrollHeight,
      behavior: "smooth",
    });
  }, []);

  const selectConversation = useCallback((conversationId: string) => {
    setSelectedConversationId(conversationId);
    setDraft("");
  }, []);

  const selectGroup = useCallback(
    (groupId: string) => {
      setSelectedGroupId(groupId);
      setExpandedGroupIds((current) =>
        current.indexOf(groupId) !== -1 ? current : [...current, groupId],
      );
      const visible = conversations.filter((item) =>
        conversationMatchesGroup(item, groupId),
      );
      const matched = visible.find((item) => item.id === groupId);
      if (matched) {
        setSelectedConversationId(matched.id);
        setDraft("");
        return;
      }
      if (!visible.some((item) => item.id === selectedConversationId)) {
        setSelectedConversationId(visible[0]?.id ?? "");
        setDraft("");
      }
    },
    [conversations, selectedConversationId],
  );

  const toggleGroup = useCallback((groupId: string) => {
    setExpandedGroupIds((current) =>
      current.indexOf(groupId) !== -1
        ? current.filter((id) => id !== groupId)
        : [...current, groupId],
    );
  }, []);

  const sendMessage = useCallback(
    async (text?: string) => {
      const content = (text ?? draft).trim();
      if (!content || !selectedConversation) return;

      const message = createLocalMessage(
        content,
        sourceData.navigation?.userName,
      );

      setConversations((current) =>
        withAppendedMessage(current, selectedConversation.id, message),
      );
      setDraft("");

      window.requestAnimationFrame(() => {
        scrollMessages("bottom");
      });

      await onSendMessage?.(content, {
        ...selectedConversation,
        messages: [...selectedConversation.messages, message],
        preview: content,
        timestamp: "刚刚",
      });
    },
    [draft, onSendMessage, scrollMessages, selectedConversation, sourceData.navigation?.userName],
  );

  return {
    groups: sourceData.groups,
    navigation: sourceData.navigation,
    selectedGroupId,
    selectedGroupLabel:
      findGroupById(sourceData.groups, selectedGroupId)?.label ?? "📬 全部消息",
    conversations,
    visibleConversations,
    selectedConversation: selectedConversation as Conversation | undefined,
    selectedConversationId: selectedConversation?.id ?? "",
    expandedGroupIds,
    draft,
    setDraft,
    selectConversation,
    selectGroup,
    toggleGroup,
    sendMessage,
    scrollMessages,
    messageListRef,
    conversationListRef,
  };
}
